import { TokenService } from 'src/app/services/token/token.service';
import { Component, HostListener, HostBinding, inject, OnInit, OnDestroy } from '@angular/core';
import { filter, shareReplay, take, takeUntil } from 'rxjs/operators';
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout';
import { combineLatest, Observable, map, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  selectCompanies,
  selectLoadingCompanies,
} from '../common/ui/company-context-select/company-context.selectors';
import { NavigationEnd, Router } from '@angular/router';
import { Profile } from '../services/bdoservice/users/models/profile.model';
import { KeycloakService } from 'keycloak-angular';
import packageInfo from '../../../package.json';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { environment } from '../../environments/environment';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ThemeService } from '../services/theme.service';
import { PackageInfo } from './ui/sidebar-tile/package-info.model';
import { AccountService } from '../services/account.service';
import * as Sentry from "@sentry/angular-ivy";
import { BDOIntegrationErrorDialogComponent } from '../common/components/bdointegration-error-dialog/bdointegration-error-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogHelper } from '../common/helpers/dialog-helper';
import {
  EkoLanguageService,
  NavigationItem,
  UserProfile,
  Language,
  type AppBarProfileAction,
  EkoThemeService,
  EkoBrowserTitleService,
  hideIndexLoadingScreen,
} from '@ekowitryna/ui';
import { PackageDisplayService } from '../core/services/package-display.service';
import { EKOWITRYNA_SUBSCRIPTION_URL } from '../core/constants/subscription-url';

const MOBILE_BREAKPOINT_PX = 1024;
const SUPPORTED_LANGUAGE_CODES = ['pl', 'en', 'de', 'es', 'uk', 'fr'] as const;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  standalone: false,
  providers: [ConfirmationService, DialogService, MessageService, TranslateService]
})

export class DashboardComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly keycloak = inject(KeycloakService);
  private readonly gtmService = inject(GoogleTagManagerService);
  protected readonly themeService = inject(ThemeService);
  private readonly messageService = inject(MessageService);
  private readonly accountService = inject(AccountService);
  private readonly tokenService = inject(TokenService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly languageService = inject(EkoLanguageService);
  private readonly store = inject(Store);
  private readonly packageDisplay = inject(PackageDisplayService);
  private readonly darkModeService = inject(EkoThemeService);
  private readonly browserTitle = inject(EkoBrowserTitleService);

  appVersion = '0.0.0';
  isInitializing = true;

  isHandset = false;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay(1)
    );

  profile: Profile;

  sidebarVisible = false;
  sidebarWidth = 320;
  isResizing = false;
  isCollapsed = false; // State of the sidebar

  readonly MIN_WIDTH = 25;
  readonly MAX_WIDTH = 320;

  activePackage: PackageInfo | null = null;
  inactivePackages: PackageInfo[] = [];

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    this.sidebarWidth = this.isCollapsed ? this.MIN_WIDTH : this.MAX_WIDTH;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.isResizing) {
      // Calculate the new width
      let newWidth = event.clientX;

      // Enforce minimum and maximum bounds
      if (newWidth < this.MIN_WIDTH) {
        newWidth = this.MIN_WIDTH;
        this.isCollapsed = true;
      } else if (newWidth > this.MAX_WIDTH) {
        newWidth = this.MAX_WIDTH;
        this.isCollapsed = false;
      }

      // Update the sidebar width
      this.sidebarWidth = newWidth;
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp() {
    if (this.isResizing) {
      this.isResizing = false;
    }
  }

  startResizing(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;
  }

  temporarilyShowSidebar() {
    if (this.isCollapsed) {
      this.sidebarWidth = this.MAX_WIDTH;
    }
  }

  onMouseLeaveSidebar() {
    this.showToggleButton = this.isCollapsed;

    if (this.isCollapsed) {
      this.sidebarWidth = this.MIN_WIDTH;
    }
  }

  menuItems: MenuItem[] = [];

  // Ekowitryna UI properties
  currentUser: UserProfile | null = null;
  authenticated = false;
  profileActions: AppBarProfileAction[] = [];
  navigationItems: NavigationItem[] = [];
  pageTitle = 'BDO Mobile';
  currentRoute = '';
  currentLanguage = '';
  availableLanguages: Language[] = [
    { code: 'pl', name: 'Polski' },
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
    { code: 'uk', name: 'Українська', flag: 'ua' },
    { code: 'fr', name: 'Français' }
  ];
  sidebarCollapsed = false;
  isMobile = false;
  keycloakUrl: string = environment.keycloakUrl;
  keycloakRealm: string = environment.realm;
  keycloakClientId: string = environment.clientId;
  profileExternalUrl = `${environment.platformWebUrl.replace(/\/$/, '')}/profile`;
  readonly subscriptionUrl = EKOWITRYNA_SUBSCRIPTION_URL;

  loginLabel = '';
  userMenuLabel = '';
  myProfileLabel = '';
  logoutLabel = '';
  buySubscriptionLabel = '';
  themeLabel = '';
  languageLabel = '';
  toggleSidebarLabel = '';

  requireFirstConfiguration = false;
  requireCompanyData = false;
  private entitySetupDialog: DynamicDialogRef | undefined;
  private entitySetupDialogOpened = false;
  showToggleButton = false;

  @HostBinding('class.sidebar-open-mobile')
  get sidebarOpenMobile(): boolean {
    return this.isMobile && !this.sidebarCollapsed;
  }

  get showFirstConfigurationOverlay(): boolean {
    return this.requireFirstConfiguration && this.authenticated;
  }

  get showCompanyDataOverlay(): boolean {
    return this.requireCompanyData
      && this.authenticated
      && !this.requireFirstConfiguration;
  }

  @HostBinding('attr.data-initialized')
  get dashboardInitialized(): string | null {
    return this.isInitializing ? null : 'true';
  }

  private keycloakUserReady = false;
  private profileReady = false;
  private callbackCleanupScheduled = false;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.showFirstConfigurationOverlay || this.showCompanyDataOverlay) {
      event.preventDefault();
      event.stopPropagation();
    }
  }


  changeExpandSubmenu(item) {
    item.showChildrens = !item.showChildrens;
  }

  refreshPackageInfo() {
    this.accountService.loadProfile();
  }

  getFullName(): string {
    return `${this.profile?.firstName} ${this.profile?.lastName}`;
  }

  toggleNightMode(): void {
    this.themeService.toogleTheme();
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }

    this.authenticated = this.keycloak.isLoggedIn();
    void this.initUserFromKeycloak();

    const companyContext = this.tokenService.getCompanyContext();

    // Initialize language
    this.currentLanguage = this.languageService.currentLanguage;
    this.setupTranslation();
    this.setupRouteTracking();

    this.initMenu();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      const gtmTag = {
        event: 'page',
        pageName: event.url
      };
      this.gtmService.pushTag(gtmTag);
    });

    this.isHandset$.subscribe(isHandset => {
      this.isHandset = isHandset;
    });

    this.accountService.activePackage$.pipe(takeUntil(this.destroy$)).subscribe(activePackage => {
      if (activePackage) {
        this.activePackage = new PackageInfo(
          activePackage.name,
          activePackage.operationCount,
          100,
          new Date(activePackage.endDate),
          activePackage.activated,
        );
      }
    });

    this.accountService.inactivePackages$.pipe(takeUntil(this.destroy$)).subscribe(inactivePackages => {
      this.inactivePackages = inactivePackages.map(pkg => new PackageInfo(
        pkg.name,
        pkg.operationCount,
        100,
        new Date(pkg.endDate),
        pkg.activated,
      ));
    });

    this.accountService.profile$.pipe(takeUntil(this.destroy$)).subscribe(profileState => {
      if (!profileState || profileState.loading) {
        return;
      }

      this.profileReady = true;

      if (profileState.requireFirstConfiguration) {
        this.requireFirstConfiguration = true;
        this.requireCompanyData = false;
      } else {
        this.requireFirstConfiguration = false;
        this.requireCompanyData = profileState.requireCompanyData ?? false;
        if (!this.requireCompanyData) {
          this.checkIsBDOCompany();
        }
      }

      if (profileState.data) {
        if (this.keycloak.getUsername() !== companyContext?.username) {
          this.tokenService.removeCompanyContext();
        }

        this.profile = profileState.data;

        this.currentUser = {
          name: this.profile.firstName && this.profile.lastName
            ? `${this.profile.firstName} ${this.profile.lastName}`
            : this.keycloak.getUsername() || 'User',
          email: this.keycloak.getUsername() || '',
          role: this.keycloak.getUserRoles().join(', ') || 'User'
        };
        this.applySubscriptionBadge();
      }

      this.tryFinishInitializing();
    });


    this.accountService.loadProfile();

    this.appVersion = packageInfo.version;

    const keycloakInstance = this.keycloak.getKeycloakInstance();

    keycloakInstance.onTokenExpired = () => {

      keycloakInstance.updateToken(30).then(() => {/*empty*/ }).catch(() => {
        console.error('failed to get a new token');
        this.redirectToLogin()
      });

      keycloakInstance.onAuthError = (error) => {
        if (error.error === "invalid_token" || error.error === "access_denied") {
          console.log("Token wygasł lub jest nieprawidłowy. Przekierowanie do strony logowania.");
          keycloakInstance.logout({ redirectUri: window.location.origin });
          this.redirectToLogin()
        }
      };
    }

  }

  checkIsBDOCompany(): void {
    if (this.entitySetupDialogOpened) {
      return;
    }

    combineLatest([
      this.store.select(selectLoadingCompanies),
      this.store.select(selectCompanies),
    ]).pipe(
      filter(([loading]) => !loading),
      take(1),
      takeUntil(this.destroy$),
    ).subscribe(([, companies]) => {
      if (!companies?.length) {
        this.openBDOIntegrationErrorDialog();
      }
    });
  }

  private tryFinishInitializing(): void {
    if (!this.keycloakUserReady || !this.profileReady) {
      return;
    }

    if (this.isKeycloakCallbackInProgress()) {
      this.scheduleCallbackCleanupCheck();
      return;
    }

    this.finishInitialization();
  }

  private finishInitialization(): void {
    if (!this.isInitializing) {
      return;
    }

    this.isInitializing = false;
    hideIndexLoadingScreen();
  }

  private scheduleCallbackCleanupCheck(): void {
    if (this.callbackCleanupScheduled) {
      return;
    }

    this.callbackCleanupScheduled = true;
    const maxRetries = 50;
    let retries = 0;

    const check = () => {
      if (!this.isKeycloakCallbackInProgress() || retries >= maxRetries) {
        this.callbackCleanupScheduled = false;
        this.finishInitialization();
        return;
      }

      retries += 1;
      setTimeout(check, 100);
    };

    setTimeout(check, 100);
  }

  private isKeycloakCallbackInProgress(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const hash = window.location.hash || '';
    return (
      hash.includes('state=') ||
      hash.includes('session_state=') ||
      hash.includes('code=') ||
      hash.includes('iss=') ||
      hash.includes('error=')
    );
  }

  /** Ustaw avatar z Keycloak od razu (jak platforma-web), zanim wróci profil z API. */
  private async initUserFromKeycloak(): Promise<void> {
    if (!this.authenticated) {
      this.darkModeService.initFromStorageOrSystem();
      this.languageService.initializeLanguage([...SUPPORTED_LANGUAGE_CODES]);
      this.keycloakUserReady = true;
      this.tryFinishInitializing();
      return;
    }

    await this.darkModeService.syncFromProfile();
    await this.languageService.syncFromProfile([...SUPPORTED_LANGUAGE_CODES]);

    try {
      const profile = await this.keycloak.loadUserProfile();
      const name = `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
        || profile.username
        || this.keycloak.getUsername()
        || 'User';

      this.currentUser = {
        name,
        email: profile.email || this.keycloak.getUsername() || '',
        role: this.keycloak.getUserRoles().join(', ') || 'User',
      };
      this.applySubscriptionBadge();
    } catch {
      const username = this.keycloak.getUsername() || 'User';
      this.currentUser = {
        name: username,
        email: username,
        role: this.keycloak.getUserRoles().join(', ') || 'User',
      };
      this.applySubscriptionBadge();
    } finally {
      this.keycloakUserReady = true;
      this.tryFinishInitializing();
    }
  }

  openBDOIntegrationErrorDialog(): void {
    if (this.entitySetupDialogOpened) {
      return;
    }

    this.entitySetupDialogOpened = true;
    this.entitySetupDialog = this.dialogService.open(BDOIntegrationErrorDialogComponent, DialogHelper.createConfig({
      closable: false,
      showHeader: false,
      width: this.isHandset ? '100vw' : '50vw',
    }));
  }



  navigateToSettings() {
    this.router.navigate(['dashboard/profile']);
  }

  initMenu() {
    const roles = this.keycloak.getUserRoles();
    this.menuItems = [
      {
        label: this.translate.instant('menu.wasteCards.label'),
        icon: 'pi pi-folder-open',
        visible: false,
        items: [
          { label: this.translate.instant('menu.wasteCards.list'), icon: 'pi pi-list', routerLink: ['/dashboard/subscription'] },
          { label: this.translate.instant('menu.wasteCards.new'), icon: 'pi pi-plus-circle', routerLink: ['/dashboard/subscription'] }
        ],
      },
      {
        label: this.translate.instant('menu.wasteCards.label'),
        icon: 'pi pi-folder',
        items: [
          { label: this.translate.instant('menu.wasteCards.list'), icon: 'pi pi-list', routerLink: ['/dashboard/waste-cards/index'] },
          { label: this.translate.instant('menu.wasteCards.new'), icon: 'pi pi-plus-circle', routerLink: ['/dashboard/waste-cards/planned'] }
        ],
      },
      {
        label: this.translate.instant('menu.wasteRegister.label'),
        icon: 'pi pi-book',
        routerLink: ['/dashboard/waste-register'],
        items: [
          { label: this.translate.instant('menu.wasteRegister.list'), icon: 'pi pi-list', routerLink: ['/dashboard/waste-register/record-search'] },
          { label: this.translate.instant('menu.wasteRegister.new'), icon: 'pi pi-plus-circle', routerLink: ['/dashboard/waste-register/search'] }
        ],
        expanded: false
      },
      { label: this.translate.instant('menu.packaging'), icon: 'pi pi-inbox', routerLink: ['/dashboard/packaging'], routerLinkActiveOptions: { exact: true }, visible: this.hasPackagingRole() },
      { label: this.translate.instant('menu.packaging'), icon: 'pi pi-inbox', badge: this.translate.instant('menu.noAccess'), routerLink: ['/dashboard/subscription'], visible: !this.hasPackagingRole() },
      { label: this.translate.instant('menu.reports'), icon: 'pi pi-table', routerLink: ['/dashboard/subscription'], routerLinkActiveOptions: { exact: true }, visible: !this.hasRole('REPORTS', roles) && this.hasRole('COMPANY', roles) },
      { label: this.translate.instant('menu.reports'), icon: 'pi pi-table', routerLink: ['/dashboard/reports/index'], routerLinkActiveOptions: { exact: true }, visible: this.hasRole('REPORTS', roles) && this.hasRole('COMPANY', roles) },
      {
        label: this.translate.instant('menu.statements.label'), icon: 'pi pi-file', visible: this.hasRole('ADMIN', roles) && this.hasRole('COMPANY', roles),
        items: [
          { label: this.translate.instant('menu.statements.packaging'), icon: 'pi pi-file', routerLink: ['/dashboard/productreport/list'], routerLinkActiveOptions: { exact: true } },
          { label: this.translate.instant('menu.statements.waste'), icon: 'pi pi-file', routerLink: ['/dashboard/manufacturingreport/list'], routerLinkActiveOptions: { exact: true } },
          { label: this.translate.instant('menu.statements.admin'), icon: 'pi pi-file', routerLink: ['/dashboard/productreport/admin/companies'], routerLinkActiveOptions: { exact: true } }
        ],
      },
      {
        label: this.translate.instant('menu.administration.label'), icon: 'pi pi-cog', visible: this.hasRole('ADMIN', roles),
        items: [
          { label: this.translate.instant('menu.administration.automaticEvidence'), icon: 'pi pi-bolt', routerLink: ['/dashboard/admin/evidence/automatic-evidence/overview'], routerLinkActiveOptions: { exact: true } },
          { label: this.translate.instant('menu.administration.adminDuplicates'), icon: 'pi pi-copy', routerLink: ['/dashboard/admin/evidence/admin-duplicates'], routerLinkActiveOptions: { exact: true } },
          { label: this.translate.instant('menu.administration.evidenceOverrides'), icon: 'pi pi-sliders-h', routerLink: ['/dashboard/admin/evidence/automatic-evidence/overrides'], routerLinkActiveOptions: { exact: true } },
          { label: this.translate.instant('menu.administration.evidenceIntervals'), icon: 'pi pi-history', routerLink: ['/dashboard/admin/evidence/automatic-evidence/intervals'], routerLinkActiveOptions: { exact: true } },
          { label: this.translate.instant('menu.administration.evidenceSettings'), icon: 'pi pi-cog', routerLink: ['/dashboard/admin/evidence/automatic-evidence/settings'], routerLinkActiveOptions: { exact: true } }
        ],
      },
      {
        label: this.translate.instant('menu.companies'), icon: 'pi pi-building', routerLink: ['/dashboard/companies'], routerLinkActiveOptions: { exact: true }, visible: this.hasRole('COMPANY', roles),
      },
      {
        label: this.translate.instant('menu.users'), icon: 'pi pi-users', routerLink: ['/dashboard/users'], routerLinkActiveOptions: { exact: true }, visible: this.hasRole('COMPANY', roles),
      },
      { label: this.translate.instant('menu.store'), icon: 'pi pi-shopping-cart', routerLink: ['/dashboard/store/plans'], routerLinkActiveOptions: { exact: true }, visible: this.hasRole('COMPANY', roles) },
    ];
  }

  hasRole(role: string, availableRoles: string[]): boolean {
    return availableRoles.indexOf(role) > -1
  }

  hasPackagingRole(): boolean {
    return this.hasClientRole('access', 'packaging-management-service') || this.hasClientRole('access_write_only', 'packaging-management-service');
  }

  hasClientRole(role: string, clientName: string): boolean {
    return this.keycloak.isUserInRole(role, clientName);
  }

  redirectToLogin() {
    this.messageService.add({ severity: 'error', summary: 'Błąd', detail: 'Wystąpił błąd podczas logowania. Spróbuj ponownie.' });
    this.router.navigate(['login']).then(() => console.log('redirect to login'));
  }


  logout(): void {
    Sentry.setUser(null);
    this.keycloak.logout(window.location.origin);
  }

  // Ekowitryna UI methods
  private setupTranslation() {
    // Subscribe to language changes from EkoLanguageService
    this.languageService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang: string) => {
        this.currentLanguage = lang;
      });

    // Subscribe to TranslateService language changes to update UI
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePageTitle();
        this.updateNavigationItems();
        this.refreshAppBarLabels();
      });

    // Initial update
    this.updatePageTitle();
    this.updateNavigationItems();
    this.refreshAppBarLabels();
  }

  private refreshAppBarLabels(): void {
    const keys = [
      'headerProfile.login',
      'headerProfile.myProfile',
      'headerProfile.userMenu',
      'headerProfile.logout',
      'headerProfile.buySubscription',
      'headerProfile.consultations',
      'settings.theme',
      'common.language',
      'a11y.toggleSidebar',
    ];
    this.translate.get(keys).subscribe(translations => {
      this.loginLabel = translations['headerProfile.login'] ?? '';
      this.userMenuLabel = translations['headerProfile.userMenu'] ?? '';
      this.myProfileLabel = translations['headerProfile.myProfile'] ?? '';
      this.logoutLabel = translations['headerProfile.logout'] ?? '';
      this.buySubscriptionLabel = translations['headerProfile.buySubscription'] ?? '';
      this.themeLabel = translations['settings.theme'] ?? '';
      this.languageLabel = translations['common.language'] ?? '';
      this.toggleSidebarLabel = translations['a11y.toggleSidebar'] ?? '';

      this.profileActions = [
        {
          label: translations['headerProfile.consultations'] ?? '',
          action: 'consultations',
          icon: 'pi pi-calendar',
        },
      ];
    });
  }

  private applySubscriptionBadge(): void {
    if (!this.currentUser) {
      return;
    }

    this.currentUser = { ...this.currentUser, subscriptionBadge: 'Free' };
    this.packageDisplay.getPackageDisplayName$().pipe(take(1)).subscribe({
      next: (name) => {
        if (!this.currentUser) {
          return;
        }
        const badge = name?.trim() || 'Free';
        if (this.currentUser.subscriptionBadge === badge) {
          return;
        }
        this.currentUser = { ...this.currentUser, subscriptionBadge: badge };
      },
      error: () => {
        if (!this.currentUser) {
          return;
        }
        this.currentUser = { ...this.currentUser, subscriptionBadge: 'Free' };
      },
    });
  }

  private setupRouteTracking() {
    // Track current route for navigation menu highlighting
    this.currentRoute = this.router.url;
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;
      });
  }

  private updatePageTitle() {
    this.translate.get('ADMIN_PANEL_TITLE').subscribe(
      (translatedTitle: string) => {
        this.pageTitle = translatedTitle || 'BDO Mobile';
      }
    );
  }

  private updateNavigationItems() {
    const roles = this.keycloak.getUserRoles();

    this.translate.get([
      'menu.companies',
      'menu.wasteCards.label',
      'menu.wasteCards.list',
      'menu.wasteCards.new',
      'menu.wasteRegister.label',
      'menu.wasteRegister.list',
      'menu.wasteRegister.new',
      'menu.packaging',
      'menu.reports',
      'menu.statements.label',
      'menu.statements.packaging',
      'menu.statements.waste',
      'menu.statements.admin',
      'menu.administration.label',
      'menu.administration.automaticEvidence',
      'menu.administration.adminDuplicates',
      'menu.users',
      'menu.store',
    ]).subscribe(translations => {
      this.navigationItems = [
        {
          label: translations['menu.wasteCards.label'] || 'Karty odpadów',
          icon: 'pi pi-folder',
          expanded: false,
          items: [
            {
              label: translations['menu.wasteCards.list'] || 'Lista KPO/KPOK',
              icon: 'pi pi-list',
              routerLink: '/dashboard/waste-cards/index'
            },
            {
              label: translations['menu.wasteCards.new'] || 'Nowa KPO/KPOK',
              icon: 'pi pi-plus-circle',
              routerLink: '/dashboard/waste-cards/planned'
            }
          ]
        },
        {
          label: translations['menu.wasteRegister.label'] || 'Ewidencja odpadów',
          icon: 'pi pi-book',
          expanded: false,
          items: [
            {
              label: translations['menu.wasteRegister.list'] || 'Lista KEO/KEOK',
              icon: 'pi pi-list',
              routerLink: '/dashboard/waste-register/record-search'
            },
            {
              label: translations['menu.wasteRegister.new'] || 'Nowy wpis',
              icon: 'pi pi-plus-circle',
              routerLink: '/dashboard/waste-register/search'
            }
          ]
        },
        {
          label: translations['menu.packaging'] || 'Opakowania',
          icon: 'pi pi-inbox',
          routerLink: '/dashboard/packaging',
          visible: this.hasPackagingRole(),
          badge: !this.hasPackagingRole() ? 'Brak dostępu' : undefined
        },
        {
          label: translations['menu.packaging'] || 'Opakowania',
          icon: 'pi pi-inbox',
          routerLink: '/dashboard/subscription',
          badge: 'Brak dostępu',
          visible: !this.hasPackagingRole()
        },
        {
          label: translations['menu.reports'] || 'Raporty',
          icon: 'pi pi-table',
          routerLink: '/dashboard/subscription',
          visible: !this.hasRole('REPORTS', roles) && this.hasRole('COMPANY', roles)
        },
        {
          label: translations['menu.reports'] || 'Raporty',
          icon: 'pi pi-table',
          routerLink: '/dashboard/reports/index',
          visible: this.hasRole('REPORTS', roles) && this.hasRole('COMPANY', roles)
        },
        {
          label: translations['menu.statements.label'] || 'Sprawozdania',
          icon: 'pi pi-file',
          visible: this.hasRole('ADMIN', roles) && this.hasRole('COMPANY', roles),
          expanded: false,
          items: [
            {
              label: translations['menu.statements.packaging'] || 'Opakowania',
              icon: 'pi pi-file',
              routerLink: '/dashboard/productreport/list'
            },
            {
              label: translations['menu.statements.waste'] || 'Odpady',
              icon: 'pi pi-file',
              routerLink: '/dashboard/manufacturingreport/list'
            },
            {
              label: translations['menu.statements.admin'] || 'Tryb administracyjny',
              icon: 'pi pi-file',
              routerLink: '/dashboard/productreport/admin/companies'
            }
          ]
        },
        {
          label: translations['menu.administration.label'] || 'Administracja',
          icon: 'pi pi-cog',
          visible: this.hasRole('ADMIN', roles),
          expanded: false,
          items: [
            {
              label: translations['menu.administration.automaticEvidence'] || 'Automatyczna ewidencja',
              icon: 'pi pi-bolt',
              routerLink: '/dashboard/admin/evidence/automatic-evidence/overview'
            },
            {
              label: translations['menu.administration.adminDuplicates'] || 'Duplikaty (Admin)',
              icon: 'pi pi-copy',
              routerLink: '/dashboard/admin/evidence/admin-duplicates'
            },
            {
              label: translations['menu.administration.evidenceOverrides'] || 'Nadpisania zasad automatu',
              icon: 'pi pi-sliders-h',
              routerLink: '/dashboard/admin/evidence/automatic-evidence/overrides'
            },
            {
              label: translations['menu.administration.evidenceIntervals'] || 'Interwały automatu',
              icon: 'pi pi-history',
              routerLink: '/dashboard/admin/evidence/automatic-evidence/intervals'
            },
            {
              label: translations['menu.administration.evidenceSettings'] || 'Ustawienia automatu',
              icon: 'pi pi-cog',
              routerLink: '/dashboard/admin/evidence/automatic-evidence/settings'
            }
          ]
        },
        {
          label: translations['menu.companies'] || 'Lista podmiotów',
          icon: 'pi pi-building',
          routerLink: '/dashboard/companies',
          visible: this.hasRole('COMPANY', roles)
        },
        {
          label: translations['menu.users'] || 'Użytkownicy',
          icon: 'pi pi-users',
          routerLink: '/dashboard/users',
          visible: this.hasRole('COMPANY', roles)
        },
        {
          label: translations['menu.store'] || 'Sklep',
          icon: 'pi pi-shopping-cart',
          routerLink: '/dashboard/store/plans',
          visible: this.hasRole('COMPANY', roles)
        },
      ].filter(item => item.visible !== false);
      this.browserTitle.setNavigationItems(this.navigationItems);
    });
  }

  onMenuClick(item: NavigationItem) {
    if (item.routerLink && !item.disabled) {
      const route = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(route);
    }
    this.closeSidebarMobile();
  }

  onProfileAction(action: string) {
    if (action === 'logout') {
      this.logout();
    } else if (action === 'consultations') {
      window.open('https://ekowitryna.pl/konsultacje/', '_blank');
    }
  }

  onMenuClosed() {
    this.closeSidebarMobile();
  }

  onSidebarToggle() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  closeSidebarMobile(): void {
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }
  }

  onLanguageChange(lang: string): void {
    this.languageService.setLanguage(lang);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
